'use client'

import { isChannelKind } from '@brew/auth/principals'
import { Button } from '@brew/ui/button'
import { TabsContent } from '@brew/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@brew/ui/tooltip'
import { SignInButton } from '@clerk/nextjs'
import {
  CheckCircle2,
  Lock,
  Settings2,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import dynamic from 'next/dynamic'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { InboundGenerateUrlButton } from '@/components/integrations/inbound-generate-url-button'
import { useOrgContext } from '@/contexts/org-context'
import {
  getCapabilitiesForProvider,
  getIntegrationTypeLabel,
  type IntegrationMeta,
} from '@/lib/integrations/catalog'
import { isInboundProvider } from '@/lib/integrations/inbound/presenters'
import { FramerFormGuide } from '../components/framer-form-guide'
import { buildTriggerableCategoriesForIntegration } from '../components/inbound-events-data'
import { InboundEventsSection } from '../components/inbound-events-section'
import { SlackFormSkeleton } from '../components/slack-form-skeleton'
import {
  focusSlackSection,
  startSlackInstall,
} from '../components/slack-page-actions'
import { TutorialSteps } from '../components/tutorial-steps'
import {
  getProviderConfig,
  type ProviderSettings,
} from '../config/provider-configs'
import { useIntegration } from '../hooks/use-integration'
import { IntegrationDetailFrame } from './integration-detail-frame'
import '../integrations.css'
import { cn } from '@brew/ui/cn'
import { TYPE } from '@/lib/ui/typography'
import { IntegrationHeaderInjector } from './integration-header-injector'
import {
  BodyGrid,
  Hero,
  MetaRail,
  TabBar,
  type TabDef,
} from './integration-page-shell'
import { CtaBlock, StatusPill } from './integration-sidebar'
import { ProseSection } from './prose-section'

/**
 * Provider-specific heavy subtrees, split out of the shared provider-page
 * chunk with `next/dynamic`: this ONE client component renders EVERY
 * `/integrations/[provider]` page, so static imports shipped the entire
 * Stripe + Slack + inbound settings surface to a lean API-key provider page
 * (and to the public `/browse/integrations` mirror). All four render behind
 * tabs / provider conditionals, below the fold — a null loading fallback is
 * a one-frame pop-in on first tab open. Slack's panel is the Setup tab a
 * Slack visit opens on, so it loads behind the skeleton the form itself
 * shows until its queries answer.
 */
const FramerForms = dynamic(
  () => import('../components/framer-forms').then((m) => m.FramerForms),
  { ssr: false }
)

const SlackForm = dynamic(
  () =>
    import('../components/slack-form').then((m) => ({ default: m.SlackForm })),
  { ssr: false, loading: () => <SlackFormSkeleton /> }
)
const StripeScopesBlock = dynamic(
  () =>
    import('../components/stripe-scopes-block').then((m) => ({
      default: m.StripeScopesBlock,
    })),
  { ssr: false, loading: () => null }
)
const StripeSettings = dynamic(
  () =>
    import('../components/stripe-settings').then((m) => ({
      default: m.StripeSettings,
    })),
  { ssr: false, loading: () => null }
)
const InboundProviderSettings = dynamic(
  () =>
    import('../components/inbound-provider-settings').then((m) => ({
      default: m.InboundProviderSettings,
    })),
  { ssr: false, loading: () => null }
)

type IntegrationPageMode = 'app' | 'public'

interface IntegrationPageProps {
  integration: IntegrationMeta
  mode: IntegrationPageMode
  /**
   * When the public browse route renders `<PageHero>` above this page,
   * skip the duplicate inline hero in `PublicSurface`.
   */
  marketingLayout?: boolean
}

/* -------------------------------------------------------------------------- */
/*  Top-level page                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Attio-style integration detail page.
 *
 * Layout rules:
 *
 *  1. **No marketing chrome.** The page is a settings panel: a
 *     compact hero with the connect CTA in the top-right, a thin meta
 *     rail on the left, and the tabbed setup/manage panes on the
 *     right. Hairline rules are the only structural marks.
 *  2. **Two-column body.** Left rail is a thin meta column —
 *     Tutorial/Documentation thumbnail, Category, Type · Direction,
 *     Pricing, Author, Capabilities, Resources. Right column is the
 *     tabbed content (Overview / Setup / Manage / Security).
 *  3. **Exactly one CTA per state.** OAuth, connected, coming-soon
 *     and the public sign-in CTA live in the hero top-right. The app
 *     shell header carries only the breadcrumb — no duplicate connect
 *     button — so the hero CTA is the single connect/disconnect action.
 *     API-key providers with inline tutorial forms hold the same
 *     hero CTA disabled until the form is valid — no duplicate
 *     submit at the bottom of the form.
 *
 * Mode contract:
 *  - `'app'`: live brand-scoped Convex state via `useIntegration`.
 *    The Connect button drives the right per-provider action (OAuth
 *    redirect, API-key form submit, Slack OAuth install).
 *  - `'public'`: no `useIntegration`. Hero CTA is a Clerk
 *    `<SignInButton>`; Setup tab shows the same prompt; Manage and
 *    Security depend on connected state and are gated accordingly.
 */
export function IntegrationPage({
  integration,
  mode,
  marketingLayout = false,
}: IntegrationPageProps) {
  const isPublic = mode === 'public'

  let body = <AppSurface integration={integration} />
  if (integration.id === 'framer') {
    body = <FramerForms />
  }
  if (isPublic) {
    body = (
      <PublicSurface
        integration={integration}
        marketingLayout={marketingLayout}
      />
    )
  }

  return (
    <IntegrationDetailFrame surface={isPublic ? 'public' : 'app'}>
      {body}
    </IntegrationDetailFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  Setup helpers (shared by PUBLIC + APP surfaces)                           */
/* -------------------------------------------------------------------------- */

function SetupOAuthBlurb({ integration }: { integration: IntegrationMeta }) {
  return (
    <p>
      One-click install via OAuth. {integration.name} handles the handshake and
      only returns scoped access tokens — Brew never sees your password. Use the
      Connect button above to start; disconnect at any time.
    </p>
  )
}

function SetupComingSoon({ integration }: { integration: IntegrationMeta }) {
  return (
    <p>
      We&apos;re building the {integration.name} integration. Want priority
      access? Email{' '}
      <a
        href={`mailto:support@brew.new?subject=${encodeURIComponent(
          `${integration.name} integration`
        )}`}
        className="text-foreground underline-offset-4 hover:text-foreground/80 hover:underline"
      >
        support@brew.new
      </a>{' '}
      and we&apos;ll loop you in.
    </p>
  )
}

function ConnectedSummary({
  integration,
  apiKeyLastFour,
  portalName,
  isApiKeyProvider,
}: {
  integration: IntegrationMeta
  apiKeyLastFour: string | undefined
  portalName: string | undefined
  isApiKeyProvider: boolean
}) {
  let detail: ReactNode
  if (portalName) {
    detail = (
      <>
        connected to <strong>{portalName}</strong>
      </>
    )
  } else if (apiKeyLastFour && isApiKeyProvider) {
    const credentialLabel = getIntegrationTypeLabel(
      integration.integrationType
    ).toLowerCase()
    detail = (
      <>
        connected with {credentialLabel} ending{' '}
        <code className="font-mono text-xs">****{apiKeyLastFour}</code>
      </>
    )
  } else {
    detail = <>connected</>
  }

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border px-3.5 py-2.5">
      <CheckCircle2
        className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
        aria-hidden
      />
      <p className="text-foreground/85 text-sm">
        Brew is {detail} on this brand. Use Disconnect above to remove access.
        <span className="sr-only">{integration.name}</span>
      </p>
    </div>
  )
}

function PendingVerificationSummary({
  integration,
}: {
  integration: IntegrationMeta
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/8 px-3.5 py-3">
      <p className="font-medium text-foreground text-sm">
        Setup saved — waiting for a signed test event
      </p>
      <p className="text-muted-foreground text-xs leading-relaxed">
        Keep the webhook URL below configured in {integration.name}. Brew will
        activate this connection automatically after the first signature-valid
        delivery.
      </p>
    </div>
  )
}

function SignInPlaceholder({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-start gap-4 rounded-lg border border-border bg-muted/20 p-6 sm:p-8">
      <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-background">
        <Lock className="size-4" aria-hidden />
      </div>
      <div className="space-y-2">
        <p className={TYPE.heading}>{title}</p>
        <p className={cn(TYPE.bodyMuted, 'max-w-[52ch] leading-relaxed')}>
          {body}
        </p>
      </div>
      <SignInButton mode="modal">
        <Button size="sm">Sign in</Button>
      </SignInButton>
    </div>
  )
}

/**
 * Why the hero button is off, on hover. A disabled button takes no pointer
 * events, so the tooltip hangs on a wrapper; the setup panel says the same
 * in words for keyboard and screen-reader users.
 */
function DisabledReasonTooltip({
  reason,
  children,
}: {
  reason: string
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  )
}

/* -------------------------------------------------------------------------- */
/*  Pure helpers                                                              */
/* -------------------------------------------------------------------------- */

function getStatusKind({
  comingSoon,
  isConnected,
  isPendingVerification,
  isError,
}: {
  comingSoon: boolean
  isConnected: boolean
  isPendingVerification?: boolean
  isError?: boolean
}):
  | 'connected'
  | 'pending_verification'
  | 'error'
  | 'available'
  | 'coming_soon' {
  if (comingSoon) {
    return 'coming_soon'
  }
  if (isConnected) {
    return 'connected'
  }
  if (isPendingVerification) {
    return 'pending_verification'
  }
  if (isError) {
    return 'error'
  }
  return 'available'
}

/**
 * The tab a visit opens on. Post-connect-rich providers open on Manage; Slack
 * opens on Setup, where its whole connect-and-configure panel lives.
 */
function resolveDefaultTab(args: {
  isManageTabVisible: boolean
  isSetupFirst: boolean
}): string {
  if (args.isManageTabVisible) {
    return 'manage'
  }
  return args.isSetupFirst ? 'setup' : 'overview'
}

interface RenderSetupBodyArgs {
  integration: IntegrationMeta
  /** Slack's own panel replaces the generic setup body. */
  isSlack: boolean
  isConnected: boolean
  isPendingVerification: boolean
  isApiKeyProvider: boolean
  isOAuthProvider: boolean
  hasSteps: boolean
  apiKeyLastFour: string | undefined
  portalName: string | undefined
  settings: ProviderSettings
  onSettingsChange: (partial: Partial<ProviderSettings>) => void
  onInboundUrlGenerated: (next: {
    connectionId: string
    webhookUrl: string
  }) => void
}

/**
 * Resolve the Setup tab body for the signed-in app surface. Pure
 * function (not a component) so it can be authored as a linear set
 * of early returns instead of nested ternaries.
 */
function renderSetupBody(args: RenderSetupBodyArgs): ReactNode {
  const {
    integration,
    isSlack,
    isConnected,
    isPendingVerification,
    isApiKeyProvider,
    isOAuthProvider,
    hasSteps,
    apiKeyLastFour,
    portalName,
    settings,
    onSettingsChange,
    onInboundUrlGenerated,
  } = args

  if (integration.availability === 'coming_soon') {
    return (
      <ProseSection title="Coming soon">
        <SetupComingSoon integration={integration} />
      </ProseSection>
    )
  }

  if (isSlack) {
    return <SlackForm />
  }

  if (isConnected) {
    return (
      <ProseSection title="Connected">
        <ConnectedSummary
          integration={integration}
          apiKeyLastFour={apiKeyLastFour}
          portalName={portalName}
          isApiKeyProvider={isApiKeyProvider}
        />
      </ProseSection>
    )
  }

  return (
    <ProseSection title="How to connect">
      {isPendingVerification ? (
        <PendingVerificationSummary integration={integration} />
      ) : null}
      {isInboundProvider(integration.id) ? (
        <InboundGenerateUrlButton
          providerId={integration.id}
          webhookUrl={settings.webhookUrl}
          onGenerated={onInboundUrlGenerated}
        />
      ) : null}
      {renderStepsOrBlurb({
        integration,
        hasSteps,
        isOAuthProvider,
        settings,
        onSettingsChange,
      })}
    </ProseSection>
  )
}

function renderStepsOrBlurb({
  integration,
  hasSteps,
  isOAuthProvider,
  settings,
  onSettingsChange,
}: {
  integration: IntegrationMeta
  hasSteps: boolean
  isOAuthProvider: boolean
  settings: ProviderSettings
  onSettingsChange: (partial: Partial<ProviderSettings>) => void
}): ReactNode {
  if (hasSteps) {
    return (
      <div className="mt-4">
        <TutorialSteps
          steps={integration.tutorialSteps!}
          settings={settings}
          onSettingsChange={onSettingsChange}
        />
      </div>
    )
  }
  if (isOAuthProvider) {
    return <SetupOAuthBlurb integration={integration} />
  }
  return (
    <p className="text-muted-foreground">
      Use the Connect button above to start setup.
    </p>
  )
}

/* -------------------------------------------------------------------------- */
/*  PUBLIC surface                                                            */
/* -------------------------------------------------------------------------- */

function PublicSurface({
  integration,
  marketingLayout = false,
}: {
  integration: IntegrationMeta
  marketingLayout?: boolean
}) {
  const [tab, setTab] = useState('overview')

  const capabilities = useMemo(
    () => getCapabilitiesForProvider(integration.id),
    [integration.id]
  )
  const triggerableCategories = useMemo(
    () => buildTriggerableCategoriesForIntegration(integration.id),
    [integration.id]
  )
  const hasTriggerableEvents = triggerableCategories.some(
    (category) => category.events.length > 0
  )

  const status = (
    <StatusPill
      kind={
        integration.availability === 'coming_soon' ? 'coming_soon' : 'available'
      }
    />
  )

  const cta =
    integration.availability === 'coming_soon' ? (
      <Button disabled variant="secondary">
        Coming soon
      </Button>
    ) : (
      <SignInButton mode="modal">
        <Button>
          {integration.id === 'framer' ? 'Add form' : 'Get started'}
        </Button>
      </SignInButton>
    )

  const tabs = useMemo<ReadonlyArray<TabDef>>(
    () => [
      { value: 'overview', label: 'Overview', icon: Sparkles },
      { value: 'setup', label: 'Setup', icon: Settings2 },
      ...(integration.id === 'stripe'
        ? [
            {
              value: 'security',
              label: 'Security',
              icon: ShieldCheck,
            } as TabDef,
          ]
        : []),
    ],
    [integration.id]
  )

  return (
    <>
      {marketingLayout ? null : (
        <Hero integration={integration} cta={cta} status={status} />
      )}
      <BodyGrid
        rail={
          <MetaRail integration={integration} capabilities={capabilities} />
        }
        content={
          <TabBar value={tab} onValueChange={setTab} tabs={tabs}>
            <TabsContent value="overview" className="mt-0 flex flex-col gap-8">
              <ProseSection title="Overview">
                <p>{integration.description}</p>
              </ProseSection>
              {hasTriggerableEvents ? (
                <InboundEventsSection
                  providerName={integration.name}
                  categories={triggerableCategories}
                />
              ) : null}
            </TabsContent>
            <TabsContent value="setup" className="mt-0 flex flex-col gap-6">
              {integration.id === 'framer' && <FramerFormGuide />}
              <SignInPlaceholder
                title={`Sign in to set up ${integration.name}`}
                body="Connecting an integration requires a Brew workspace. Sign in or create an account to install it on a brand."
              />
            </TabsContent>
            {integration.id === 'stripe' ? (
              <TabsContent
                value="security"
                className="mt-0 flex flex-col gap-6"
              >
                <ProseSection title="Read-only Stripe scopes">
                  <p className="mb-3">
                    The Stripe Connect grant requests these scopes when you
                    authorize Brew. You can revoke access at any time from your
                    Stripe dashboard.
                  </p>
                  <StripeScopesBlock />
                </ProseSection>
              </TabsContent>
            ) : null}
          </TabBar>
        }
      />
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*  APP surface                                                               */
/* -------------------------------------------------------------------------- */

function AppSurface({ integration }: { integration: IntegrationMeta }) {
  const providerConfig = getProviderConfig(integration.id)

  const [settings, setSettings] = useState<ProviderSettings>(() => ({
    apiKey: '',
    ...providerConfig?.getDefaultSettings(),
  }))

  const {
    isConnected,
    isPendingVerification,
    isConnecting,
    isDisconnecting,
    isApiKeyProvider,
    isOAuthProvider,
    apiKeyLastFour,
    portalName,
    oAuthUrl,
    integration: integrationState,
    connect,
    disconnect,
  } = useIntegration({
    provider: integration.id,
    name: integration.name,
  })

  const handleSettingsChange = useCallback(
    (partial: Partial<ProviderSettings>) => {
      setSettings((prev) => ({ ...prev, ...partial }))
    },
    []
  )

  const handleInboundUrlGenerated = useCallback(
    (next: { connectionId: string; webhookUrl: string }) => {
      setSettings((prev) => ({
        ...prev,
        connectionId: next.connectionId,
        webhookUrl: next.webhookUrl,
      }))
    },
    []
  )

  // A chat app (Slack) connects once for the whole organization, and only an
  // organization admin connects it; its panel on the Setup tab holds the
  // workspace and every setting.
  const isChatApp = isChannelKind(integration.id)
  const isSlack = integration.id === 'slack'
  const { access } = useOrgContext()
  const isOrgAdmin = access === 'admin'

  const handleConnectClick = useCallback(async () => {
    if (isChatApp) {
      return
    }
    const connectionResult = await connect(settings)
    if (connectionResult === 'active') {
      setSettings({ apiKey: '', ...providerConfig?.getDefaultSettings() })
    }
  }, [connect, isChatApp, providerConfig, settings])

  const isFormValid = providerConfig?.isValid(settings) ?? false
  const hasSteps = !!(
    integration.tutorialSteps && integration.tutorialSteps.length > 0
  )
  const capabilities = useMemo(
    () => getCapabilitiesForProvider(integration.id),
    [integration.id]
  )
  const triggerableCategories = useMemo(
    () => buildTriggerableCategoriesForIntegration(integration.id),
    [integration.id]
  )
  const hasTriggerableEvents = triggerableCategories.some(
    (category) => category.events.length > 0
  )

  const isStripeManageVisible = integration.id === 'stripe' && isConnected
  const isInboundManageVisible =
    isInboundProvider(integration.id) && isConnected
  const isManageTabVisible = isStripeManageVisible || isInboundManageVisible

  // Tab strip is filtered by what makes sense for this provider+state.
  const tabs = useMemo<ReadonlyArray<TabDef>>(() => {
    const out: Array<TabDef> = [
      { value: 'overview', label: 'Overview', icon: Sparkles },
      { value: 'setup', label: 'Setup', icon: Settings2 },
    ]
    if (isManageTabVisible) {
      out.push({ value: 'manage', label: 'Manage', icon: Sparkles })
    }
    if (integration.id === 'stripe') {
      out.push({ value: 'security', label: 'Security', icon: ShieldCheck })
    }
    return out
  }, [integration.id, isManageTabVisible])

  // Default tab: drop users into Manage if they're already connected to
  // a post-connect-rich integration (Stripe / Inbound). Otherwise
  // Overview.
  //
  // `isConnected` arrives from a Convex `useQuery` that is `undefined`
  // on first render and resolves a tick later. Computing the default
  // inside a `useState` initializer would freeze it at the loading-time
  // value (always `overview`) — even for users who are already
  // connected. Instead we store the user's explicit choice as nullable
  // and recompute the default live every render: a freshly-resolved
  // connected state can flip the default to `manage` without ever
  // overriding the user's pick if they have already clicked another
  // tab.
  const [userSelectedTab, setUserSelectedTab] = useState<string | null>(null)
  const tab =
    userSelectedTab ??
    resolveDefaultTab({ isManageTabVisible, isSetupFirst: isChatApp })
  const setTab = useCallback((next: string) => setUserSelectedTab(next), [])

  const handleDisconnectClick = useCallback(async () => {
    if (isChatApp) {
      // "Manage": disconnecting a workspace is org-admin-only and confirmed
      // in the Slack panel, beside the settings it would stop. Open Setup,
      // then bring its workspace section up with focus once it renders.
      setTab('setup')
      requestAnimationFrame(() =>
        focusSlackSection('workspace', { isScrolled: true })
      )
      return
    }
    await disconnect()
  }, [disconnect, isChatApp, setTab])

  // The hero CTA is the ONE connect / disconnect button on the page
  // for every state — OAuth, API-key form, Slack, coming-soon,
  // connected. For API-key providers the button stays disabled until
  // the inline tutorial form is fully filled, so the user fills the
  // form below then clicks the same button in the same place every
  // time. Slack's install is org-wide and admin-only: it starts here
  // for an organization admin and is disabled, with the reason on
  // hover, for everyone else.
  const handleHeroConnect = useCallback(() => {
    if (isChatApp) {
      if (isSlack && isOrgAdmin) {
        startSlackInstall()
      }
      return
    }
    if (isOAuthProvider && oAuthUrl) {
      window.location.href = oAuthUrl
      return
    }
    if (isApiKeyProvider && !isFormValid) {
      // Need credentials — switch to Setup so the user can fill the
      // form. The hero CTA stays disabled until the form is valid.
      setTab('setup')
      return
    }
    void handleConnectClick()
  }, [
    handleConnectClick,
    isApiKeyProvider,
    isChatApp,
    isFormValid,
    isOAuthProvider,
    isOrgAdmin,
    isSlack,
    oAuthUrl,
    setTab,
  ])

  const isCtaDisabled =
    !isConnected &&
    integration.availability === 'available' &&
    isApiKeyProvider &&
    hasSteps &&
    !isFormValid
  const isChatAppConnectLocked = isChatApp && !isConnected && !isOrgAdmin

  const status = (
    <StatusPill
      kind={getStatusKind({
        comingSoon: integration.availability === 'coming_soon',
        isConnected,
        isPendingVerification,
        isError: integrationState?.status === 'error',
      })}
    />
  )

  const ctaBlock = (
    <CtaBlock
      integration={integration}
      isConnected={isConnected}
      isPendingVerification={isPendingVerification}
      isConnecting={isConnecting}
      isDisconnecting={isDisconnecting}
      isOAuthProvider={isOAuthProvider}
      onConnectClick={handleHeroConnect}
      onDisconnectClick={handleDisconnectClick}
      connectedLabel={isChatApp ? 'Manage' : undefined}
      isDisabled={isCtaDisabled || isChatAppConnectLocked}
    />
  )
  const cta = isChatAppConnectLocked ? (
    <DisabledReasonTooltip
      reason={`Only an organization admin can connect ${integration.name}.`}
    >
      {ctaBlock}
    </DisabledReasonTooltip>
  ) : (
    ctaBlock
  )

  const setupBody = renderSetupBody({
    integration,
    isSlack,
    isConnected,
    isPendingVerification,
    isApiKeyProvider,
    isOAuthProvider,
    hasSteps,
    apiKeyLastFour,
    portalName,
    settings,
    onSettingsChange: handleSettingsChange,
    onInboundUrlGenerated: handleInboundUrlGenerated,
  })

  return (
    <>
      <IntegrationHeaderInjector name={integration.name} />
      <Hero integration={integration} cta={cta} status={status} />
      <BodyGrid
        rail={
          <MetaRail integration={integration} capabilities={capabilities} />
        }
        content={
          <TabBar value={tab} onValueChange={setTab} tabs={tabs}>
            <TabsContent value="overview" className="mt-0 flex flex-col gap-8">
              <ProseSection title="Overview">
                <p>{integration.description}</p>
              </ProseSection>
              {hasTriggerableEvents ? (
                <InboundEventsSection
                  providerName={integration.name}
                  categories={triggerableCategories}
                />
              ) : null}
            </TabsContent>

            <TabsContent value="setup" className="mt-0 flex flex-col gap-6">
              {setupBody}
            </TabsContent>

            {isStripeManageVisible ? (
              <TabsContent value="manage" className="mt-0">
                <ProseSection title="Manage Stripe">
                  <StripeSettings />
                </ProseSection>
              </TabsContent>
            ) : null}

            {isInboundManageVisible ? (
              <TabsContent value="manage" className="mt-0">
                <ProseSection title={`Manage ${integration.name}`}>
                  <InboundProviderSettings providerId={integration.id} />
                </ProseSection>
              </TabsContent>
            ) : null}

            {integration.id === 'stripe' ? (
              <TabsContent
                value="security"
                className="mt-0 flex flex-col gap-6"
              >
                <ProseSection title="Read-only Stripe scopes">
                  <p className="mb-3">
                    The Stripe Connect grant requests these scopes when you
                    authorize Brew. Revoke at any time from your Stripe
                    dashboard or by disconnecting from the hero above.
                  </p>
                  <StripeScopesBlock />
                </ProseSection>
              </TabsContent>
            ) : null}
          </TabBar>
        }
      />
    </>
  )
}
